// Groupes de messagerie proposés chaque mois par le worker (spec § 9) : une filiale sans site écrit avec le domaine de
// son groupe. Lus et tranchés par le seul responsable des imports (`is_imports_owner`, vérifié par la base) : un mot
// accepté sert dès la recherche de contacts suivante, un mot écarté n'est jamais reproposé.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { useNotify, useTranslate } from "ra-core";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { getSupabaseClient } from "../providers/supabase/supabase";

type Proposal = {
  id: number;
  word: string;
  domain: string;
  domain_method: "company_site" | "wikidata";
  domain_source_url: string | null;
  companies: number;
  projects: number;
  examples: string[];
  status: "pending" | "accepted" | "rejected";
};

export const MailGroupProposals = () => {
  const translate = useTranslate();
  const t = (key: string, options?: object) =>
    translate(`resources.projects.imports.groups_${key}`, options);
  const notify = useNotify();
  const queryClient = useQueryClient();
  const proposals = useQuery({
    queryKey: ["mail_group_proposals"],
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("mail_group_proposals")
        .select(
          "id, word, domain, domain_method, domain_source_url, companies, projects, examples, status",
        )
        .neq("status", "rejected")
        .order("projects", { ascending: false });
      if (error) throw error;
      return data as Proposal[];
    },
  });
  const decide = useMutation({
    mutationFn: async ({ id, accept }: { id: number; accept: boolean }) => {
      const { error } = await getSupabaseClient().rpc("decide_mail_group", {
        proposal: id,
        accept,
      });
      if (error) throw error;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["mail_group_proposals"] }),
    onError: (error: Error) => notify(error.message, { type: "error" }),
  });

  const pending = proposals.data?.filter((p) => p.status === "pending") ?? [];
  const accepted = proposals.data?.filter((p) => p.status === "accepted") ?? [];

  return (
    <Card>
      <CardContent className="flex flex-col gap-4">
        <div>
          <h3 className="font-medium">{t("title")}</h3>
          <p className="text-sm text-muted-foreground">{t("intro")}</p>
        </div>
        {proposals.error ? (
          <p className="text-sm text-destructive">{proposals.error.message}</p>
        ) : proposals.isPending ? null : !pending.length ? (
          <p className="text-sm text-muted-foreground">{t("empty")}</p>
        ) : (
          <ul className="flex flex-col divide-y">
            {pending.map((proposal) => (
              <li
                key={proposal.id}
                className="py-3 flex flex-col sm:flex-row sm:items-center gap-3"
              >
                <div className="flex-1 min-w-0 flex flex-col gap-1">
                  <span className="text-sm">
                    <span className="font-semibold">« {proposal.word} »</span> →{" "}
                    {proposal.domain}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t("counts", {
                      companies: proposal.companies,
                      projects: proposal.projects,
                    })}{" "}
                    · {proposal.examples.join(" ; ")}
                  </span>
                  <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                    {t(`method_${proposal.domain_method}`)}
                    {proposal.domain_source_url && (
                      <a
                        href={proposal.domain_source_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 underline"
                      >
                        {t("source")}
                        <ExternalLink className="size-3" />
                      </a>
                    )}
                  </span>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button
                    size="sm"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({ id: proposal.id, accept: true })
                    }
                  >
                    {t("accept")}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({ id: proposal.id, accept: false })
                    }
                  >
                    {t("reject")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
        {accepted.length > 0 && (
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">{t("accepted")}</span>
            <div className="flex flex-wrap gap-2">
              {accepted.map((proposal) => (
                <Badge key={proposal.id} variant="secondary" className="gap-2">
                  {proposal.word} → {proposal.domain}
                  <button
                    type="button"
                    className="underline text-xs"
                    disabled={decide.isPending}
                    onClick={() =>
                      decide.mutate({ id: proposal.id, accept: false })
                    }
                  >
                    {t("withdraw")}
                  </button>
                </Badge>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
